import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { GlobalState } from "../../store"
import { addErrorNotification } from "../notifications/actions"

import { thumbtableAPI } from "../../apis"
import { IThumbTable } from "."
import { fetchFormattingSettings } from "../common/userFormattingSettings/slice"
import { Collection } from "../../models"
import { IUserFormattingSettings } from "../applicationheader/components/usermenu"

export const PAGE_SIZE = 20

export const thumbTableViewSlice = createSlice({
    name: "thumbTableView",

    initialState: {
        code: "",
        collection: {
            collectionId: 0,
            isSmartList: false,
        },
        columns: [],
        areColumnsLoaded: false,
        isLoading: false,
        name: "",
        items: [],
        totalItemsCount: 0,
    } as IThumbTable,

    reducers: {
        setThumbTable: (state, action: PayloadAction<Collection>) => {
            state.collection = action.payload
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchThumbTableData.pending, (state) => {
                state.areColumnsLoaded = true
            })
            .addCase(fetchThumbTableData.fulfilled, (state, action) => {
                if (action.payload) {
                    const { name, code, columns } = action.payload
                    state.name = name
                    state.code = code
                    state.columns = columns
                    state.areColumnsLoaded = false
                }
            })
            .addCase(fetchItems.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchItems.fulfilled, (state, action) => {
                if (!action.payload) {
                    return
                }

                if (action.payload.areItemsReloaded) {
                    state.items = [...action.payload.items]
                } else {
                    state.items = [...state.items, ...action.payload.items]
                }
                state.totalItemsCount = action.payload.count
            })
    },
})

export const { setThumbTable } = thumbTableViewSlice.actions

export default thumbTableViewSlice.reducer

export const defaultRequestingFieldsErrorMessage =
    "Something went wrong when fetching the list."

export const initializeItems = createAsyncThunk(
    "thumbTableView/fetchThumbTableData",
    async (_, { dispatch }) => {
        dispatch(fetchThumbTableData())
        dispatch(fetchItems(true))
    },
)

export const fetchThumbTableData = createAsyncThunk(
    "thumbTableView/fetchThumbTableData",
    async (_, { getState, dispatch }) => {
        const state = getState() as GlobalState
        const { collection } = state.thumbTableView

        try {
            const collectionDefinition = await thumbtableAPI.getTableData(
                collection.collectionId,
            )
            return collectionDefinition
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const fetchItems = createAsyncThunk(
    "thumbTableView/fetchItems",
    async (areItemsReloaded: boolean, { getState, dispatch }) => {
        const state = getState() as GlobalState
        const { collection, items } = state.thumbTableView
        const skip = areItemsReloaded ? 0 : items.length

        let userFormattingSettings = state.userFormattingSettings

        if (!userFormattingSettings.isInitialized) {
            const response = await dispatch(fetchFormattingSettings())
            userFormattingSettings = response.payload as IUserFormattingSettings
        }

        try {
            const itemCollection = await thumbtableAPI.getItems(
                collection.collectionId,
                userFormattingSettings,
                skip,
                PAGE_SIZE,
            )
            return { ...itemCollection, areItemsReloaded }
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)
